import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});


/* ---------------- GET ALL QUESTIONS ---------------- */

export const fetchQuestions = createAsyncThunk(
  "questions/fetchQuestions",
  async (
    { search = "", page = 1, limit = 10, key = "all", letter = "", tag = "" },
    thunkAPI
  ) => {
    try {
      const queryParams = new URLSearchParams({
        key,
        search,
        page: String(page),
        limit: String(limit),
      });

      if (letter) {
        queryParams.append("letter", letter);
      }
      
      const res = await axios.get(
        `${BASE_URL}/all/questions?${queryParams.toString()}`,
        { headers: getHeaders() }
      );

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- GET QUESTION BY ID ---------------- */

export const fetchQuestionById = createAsyncThunk(
  "questions/fetchQuestionById",
  async (id, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/question/${id}`, {
        headers: getHeaders(),
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- ADD QUESTION ---------------- */

export const addQuestion = createAsyncThunk(
  "questions/addQuestion",
  async (data, thunkAPI) => {
    try {
      const payload = {
        question: data.question,
        question_dk: data.question_dk,
        options: data.options,
        tag: data.tag,
        letter: data.letter,
        active: data.active,
        version: data.version,
        correct_answers: data.correct_answers
      };

      const res = await axios.post(`${BASE_URL}/question`, payload, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- UPDATE QUESTION ---------------- */

export const updateQuestion = createAsyncThunk(
  "questions/updateQuestion",
  async ({ id, data }, thunkAPI) => {
    try {
      const payload = {
        question_id: id,
        question: data.question,
        question_dk: data.question_dk,
        options: data.options,
        tag: data.tag,
        letter: data.letter,
        active: data.active,
        version: data.version,
        correct_answers: data.correct_answers
      };

      const res = await axios.put(`${BASE_URL}/question`, payload, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- DELETE QUESTION ---------------- */

export const deleteQuestion = createAsyncThunk(
  "questions/deleteQuestion",
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/question/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- TRANSLATE QUESTION ---------------- */

export const translateQuestion = createAsyncThunk(
  "questions/translateQuestion",
  async (data, thunkAPI) => {
    try {
      const res = await axios.put(`${BASE_URL}/translate/question`, data, {
        headers: getHeaders(),
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

/* ---------------- GET ALL ACTIVE QUESTIONS ---------------- */

export const fetchActiveQuestions = createAsyncThunk(
  "questions/fetchActiveQuestions",
  async (thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/questions`, {
        headers: getHeaders(),
      });

      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchQuestionTags = createAsyncThunk(
  "questions/fetchQuestionTags",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/all/questions-tags`, {
        headers: getHeaders(),
      });

      return Array.isArray(res.data?.data) ? res.data.data : res.data || [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const fetchQuestionLetters = createAsyncThunk(
  "questions/fetchQuestionLetters",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${BASE_URL}/all/questions-letters`, {
        headers: getHeaders(),
      });

      return Array.isArray(res.data?.data) ? res.data.data : res.data || [];
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);
/* ---------------- SLICE ---------------- */

const questionSlice = createSlice({
  name: "questions",

  initialState: {
    questions: [],
    activeQuestions: [],
    questionTags: [],
    questionLetters: [],
    selectedQuestion: null,
    page: 1,
    limit: 10,
    total: 10,
    loading: false,
    singleLoading: false,
    saving: false,
    deleting: false,
    tagsLoading: false,
    lettersLoading: false,
    error: null,
  },

  reducers: {
    clearSelectedQuestion: (state) => {
      state.selectedQuestion = null;
    },
  },

  extraReducers: (builder) => {
    /* FETCH QUESTIONS */

    builder
      .addCase(fetchQuestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questions = action.payload.questions;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
      })
      .addCase(fetchQuestions.rejected, (state) => {
        state.loading = false;
      });

    /* FETCH QUESTION BY ID */

    builder
      .addCase(fetchQuestionById.pending, (state) => {
        state.singleLoading = true;
      })
      .addCase(fetchQuestionById.fulfilled, (state, action) => {
        state.singleLoading = false;
        state.selectedQuestion = action.payload;
      })
      .addCase(fetchQuestionById.rejected, (state) => {
        state.singleLoading = false;
      });

    /* ADD QUESTION */

    builder
      .addCase(addQuestion.pending, (state) => {
        state.saving = true;
      })
      .addCase(addQuestion.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(addQuestion.rejected, (state) => {
        state.saving = false;
      });

    /* UPDATE QUESTION */

    builder
      .addCase(updateQuestion.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateQuestion.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateQuestion.rejected, (state) => {
        state.saving = false;
      });

    /* DELETE QUESTION */

    builder
      .addCase(deleteQuestion.pending, (state) => {
        state.deleting = true;
      })
      .addCase(deleteQuestion.fulfilled, (state, action) => {
        state.deleting = false;

        state.questions = state.questions.filter(
          (q) => q.id !== action.payload
        );
      })
      .addCase(deleteQuestion.rejected, (state) => {
        state.deleting = false;
      });

    builder
      .addCase(translateQuestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(translateQuestion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(translateQuestion.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchActiveQuestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.activeQuestions = action.payload;
      })
      .addCase(fetchActiveQuestions.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchQuestionTags.pending, (state) => {
        state.tagsLoading = true;
      })
      .addCase(fetchQuestionTags.fulfilled, (state, action) => {
        state.tagsLoading = false;
        state.questionTags = action.payload;
      })
      .addCase(fetchQuestionTags.rejected, (state) => {
        state.tagsLoading = false;
      });

    builder
      .addCase(fetchQuestionLetters.pending, (state) => {
        state.lettersLoading = true;
      })
      .addCase(fetchQuestionLetters.fulfilled, (state, action) => {
        state.lettersLoading = false;
        state.questionLetters = action.payload;
      })
      .addCase(fetchQuestionLetters.rejected, (state) => {
        state.lettersLoading = false;
      });
  },
});

export const { clearSelectedQuestion } = questionSlice.actions;

export default questionSlice.reducer;
